import { FC } from 'react';
import cn from 'classnames';

import { DataLink, PsychicRate } from 'components/Shared/SharedComponents';
import { PsychicRatingLine } from 'entities/PsychicRatingLine';
import type { RatePerMinuetType } from 'types/objectTypes';
import { capitalizeFirstLetter } from 'lib/text.service';
import { PsychicCardLabelSmall } from 'entities/PsychicCardLabel';

import styles from './PsychicCardHeading.module.scss';

import type { IPriceComponent, IPsychicCardHeading } from '../config/declarations';

/* Nested Component */
const PriceComponent: FC<IPriceComponent> = ({ psychic, ratePerMinuteText }) => {
  const priceStyles = {
    wrapper: styles.price,
    common: styles.priceCommon,
    commonWithDiscount: styles.priceCrossed,
    discount: styles.priceDiscount,
  };

  const ratePerMinute: RatePerMinuetType = {
    discount: psychic.customerPrice,
    price: psychic.basePrice,
  };

  const minText = `/${ratePerMinuteText}`;

  return (
    <PsychicRate
      pricePerMinute={ratePerMinute.price}
      priceWithDiscountPerMinute={ratePerMinute.discount}
      classNames={priceStyles}
      time={minText}
    />
  );
};

/* Main Component */
export const PsychicCardHeading: FC<IPsychicCardHeading> = ({
  psychic,
  linkProps,
  psychicFrame,
  className,
  nameClassName,
  size = 'medium',
}) => {
  const sizeClass = styles[`size${capitalizeFirstLetter(size)}`];
  const psychicNameLength = psychic?.lineName.length;
  let fontClass = '';

  if (psychicNameLength > 15) {
    fontClass = styles.fontLong;
  } else if (psychicNameLength > 12 && psychicNameLength <= 15) {
    fontClass = styles.fontMedium;
  } else if (psychicNameLength > 9) {
    fontClass = styles.fontShort;
  } else {
    fontClass = styles.fontDefault;
  }

  return (
    <div className={cn(styles.topLine, className, sizeClass)}>
      <DataLink
        link={linkProps?.profileLink!}
        href={linkProps?.profileUrl!}
        className={cn(styles.name, fontClass, nameClassName, sizeClass)}
      >
        {psychic.lineName}
        {size === 'small' && (
          <PsychicCardLabelSmall
            psychic={psychic}
            psychicFrame={psychicFrame}
          />
        )}
      </DataLink>
      <PsychicRatingLine
        overallScore={psychic.overallScore}
        psychicFrame={psychicFrame}
        responses={psychic.responses}
        styles={styles}
      />
      <PriceComponent
        psychic={psychic}
        ratePerMinuteText={psychicFrame.ratePerMinute!}
      />
    </div>
  );
};
